import { useState, useRef, useEffect } from "react";
import { usePageTitle } from "@/hooks/use-page-title";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { RELEASES, CURRENT_VERSION, type ChangeType } from "@/lib/changelog";
import { useToast } from "@/hooks/use-toast";
import {
  Receipt,
  FileText,
  FolderKanban,
  HardHat,
  LayoutTemplate,
  Plug,
  Rocket,
  Users,
  Package,
  Wallet,
  Wrench,
  Settings,
  RefreshCw,
  ChevronRight,
  Sparkles,
  Mail,
  Bot,
  Send,
  Loader2,
  Lightbulb,
  ThumbsUp,
  ChevronDown,
  ChevronUp,
  Clock,
  ArrowLeft,
  MessageSquare,
  CheckCircle2,
  Check,
  BookOpen,
  Building2,
  Image,
  CreditCard,

  BarChart2,
  Download,
  Calendar,
  ExternalLink,
  Gift,
  CheckCheck,
  HelpCircle,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useSupportNotifications } from "@/hooks/use-support-notifications";
import { useSetupGuide } from "@/hooks/use-setup-guide";
import { CHECKLIST_ITEMS } from "@/lib/setup-checklist";


type GuideStep = { heading?: string; text: string };
type Guide = { title: string; steps: GuideStep[] };
type GuideCategory = { icon: React.ElementType; title: string; guides: Guide[] };

const guideCategories: GuideCategory[] = [
  {
    icon: Rocket,
    title: "Getting started",
    guides: [
      {
        title: "Setting up your account",
        steps: [
          { text: "Go to Settings and open Business Information. Add your company name, phone, email and address. These print on your PDFs, and notification emails go to this email address." },
          { text: "In the same section, check your timezone and currency." },
          { text: "Open Taxes and add the tax lines you charge." },
          { text: "Open Quote Template and set your target margin." },
          { text: "The Setup Guide tab on this page has a checklist of your first steps." },
        ],
      },
      {
        title: "Your trial, Free and Pro",
        steps: [
          { text: "Every new account starts with 7 days of Pro, with no credit card." },
          { text: "When the trial ends, your account moves to the Free plan by itself. Your quotes, invoices and customers all stay." },
          { text: "Free keeps unlimited quotes and invoices, customer PDFs, customers, products and field tools. Pro features lock until you upgrade." },
          { text: "To upgrade, click any Upgrade to Pro button, or go to Settings and open Billing." },
        ],
      },
      {
        title: "Cancelling Pro",
        steps: [
          { text: "Go to Settings, open Billing and click Manage Billing. This opens the Stripe customer portal, where you can cancel." },
          { text: "Pro stays active until the end of the month or year you've paid for, then your account moves to Free. A billing period that has already started isn't refunded." },
          { text: "Your records stay on the Free plan. To remove everything, go to Settings, open Danger Zone and use Delete Account. This can't be undone." },
        ],
      },
    ],
  },
  {
    icon: FileText,
    title: "Quotes",
    guides: [
      {
        title: "Creating a quote",
        steps: [
          { text: "Click Quotes in the sidebar, then New Quote." },
          { text: "Pick a customer or create a new one, then click Create Quote." },
          { text: "Click Add line item and enter a description, quantity and price. Click Link product on a line to fill it in from your Products & Services." },
          { text: "At the top, set the quote date and the Valid until date. Below the lines, add a Project Description and any Notes / Terms." },
        ],
      },
      {
        title: "Adding your costs and seeing your margin",
        steps: [
          { text: "Click Costs & Notes under a line item." },
          { text: "Under Internal Costs, search your cost catalog and pick an item, or type a name and choose Create new cost to add a one-off cost. This works on every plan." },
          { text: "On Pro, you can also tick Save to cost catalog so it's there for next time." },
          { text: "Add private notes there too, like reminders about the job." },
          { text: "The Internal Workshop panel shows your estimated profit and margin as you go. Click Change beside the target to use a different target on this quote." },
          { text: "None of this prints on the customer PDF." },
        ],
      },
      {
        title: "Adding a discount or tax",
        steps: [
          { text: "Below the line items, turn on Apply Discount and enter a percentage or a dollar amount." },
          { text: "Turn on Charge Tax and choose from the tax lines you set up in Settings." },
          { text: "The Total Customer Price updates as you go." },
        ],
      },
      {
        title: "Moving and grouping line items",
        steps: [
          { text: "Drag a line to move it, or use its up and down arrows. You can also duplicate or delete a line from there." },
          { text: "On Pro, click Add above the lines and choose Add Group to make a named section. Groups can have a color, be moved and be collapsed." },
        ],
      },
      {
        title: "Previewing, downloading and sending",
        steps: [
          { text: "Use the buttons at the top of the quote. Preview PDF shows the PDF on screen, and Export PDF downloads it." },
          { text: "The Customer PDF shows your lines and prices, with no costs or margin. On Free, download it and send it yourself." },
          { text: "The Cost Report PDF is your own copy with every cost, your margin and your notes. It's part of Pro." },
          { text: "On Pro, Send via Email sends the PDF to the customer. You can edit the subject and message first. Send a copy to myself starts ticked, so the PDF lands in your inbox too." },
        ],
      },
      {
        title: "Tracking a quote's status",
        steps: [
          { text: "Use the status button at the top of the quote: Draft, Sent, Accepted or Declined." },
          { text: "Customers don't accept quotes online. When a customer says yes, mark the quote as Accepted." },
          { text: "If an accepted quote has a deposit on it, QuoTrak offers to create the deposit invoice." },
        ],
      },
      {
        title: "Converting a quote to an invoice",
        steps: [
          { text: "Open the quote and click Convert to Invoice at the top, or use the actions menu on the Quotes list." },
          { text: "If the quote isn't marked Accepted yet, QuoTrak asks you to confirm first." },
          { text: "The new invoice has every line from the quote. Set the due date and it's ready to go." },
        ],
      },
      {
        title: "Duplicating or deleting a quote",
        steps: [
          { text: "Use the Duplicate button at the top of the quote to make a new copy with the same lines." },
          { text: "Use the Delete button to remove a quote. This is permanent." },
        ],
      },
      {
        title: "Downloading several quotes as a ZIP",
        steps: [
          { text: "On the Quotes list, tick the quotes you want." },
          { text: "Click Export ZIP and choose what to include. Customer PDFs work on any plan. Cost Reports and Both PDFs are part of Pro." },
        ],
      },
      {
        title: "Attaching files (Pro)",
        steps: [
          { text: "Open the quote and find Attachments, below the notes." },
          { text: "Click Add Files and choose images or PDFs." },
          { text: "These stay with the quote for your own use. They aren't added to the PDF." },
        ],
      },
    ],
  },
  {
    icon: Receipt,
    title: "Invoices",
    guides: [
      {
        title: "Creating an invoice",
        steps: [
          { text: "Click Invoices in the sidebar, then New Invoice. You can also convert an accepted quote." },
          { text: "Pick the customer, set the invoice number and the dates, then click Create Invoice." },
          { text: "Add your lines the same way as on a quote." },
        ],
      },
      {
        title: "Recording a payment",
        steps: [
          { text: "Open the invoice and click Record Payment." },
          { text: "Enter the amount and date. You can also choose a payment method and add a note, like a check number." },
          { text: "The invoice moves to Partial or Paid by itself." },
          { text: "On Pro, you can email the customer a receipt for the payment." },
        ],
      },
      {
        title: "What the statuses mean",
        steps: [
          { text: "Draft isn't sent yet. Sent is waiting on payment. Partial has some money in. Paid is done." },
          { text: "Overdue is past its due date." },
          { text: "Void cancels an invoice but keeps it on record, and it doesn't count toward revenue." },
          { text: "To change one yourself, use the status button at the top of the invoice." },
        ],
      },
      {
        title: "Sending overdue reminders (Pro)",
        steps: [
          { text: "Open the invoice and find the Overdue Reminders panel." },
          { text: "Turn it on and set how many days apart the reminders go out. QuoTrak emails the customer on that schedule after the due date, until the invoice is paid." },
          { text: "When the invoice is overdue and reminders are on, the bell button at the top of the invoice sends one right away." },
        ],
      },
      {
        title: "Previewing, downloading and sending",
        steps: [
          { text: "Use the buttons at the top of the invoice. Preview PDF shows it on screen, and Export PDF downloads it. On Free, download it and send it yourself." },
          { text: "On Pro, Send via Email sends the PDF to the customer. You can edit the subject and message first." },
        ],
      },
    ],
  },
  {
    icon: CreditCard,
    title: "Getting paid online (Pro)",
    guides: [
      {
        title: "Connecting Stripe",
        steps: [
          { text: "Go to Settings, open Accept Payments and click Connect Stripe." },
          { text: "Follow Stripe's steps to create or connect your account. It's free, and QuoTrak charges no fee of its own. Stripe's standard processing fees apply." },
          { text: "Stripe pays out to your bank on its usual schedule. You manage payouts in Stripe." },
        ],
      },
      {
        title: "How customers pay",
        steps: [
          { text: "Send the invoice, or mark it as Sent. QuoTrak then makes the payment link and includes it in the email and on the PDF." },
          { text: "The Online Payment panel on the invoice lets you copy the link or turn online payment off for that invoice." },
          { text: "Your customer pays by card or Apple Pay on a page hosted by Stripe. They don't need a QuoTrak account." },
        ],
      },
      {
        title: "After a customer pays",
        steps: [
          { text: "QuoTrak records the payment and marks the invoice Paid, or Partial if there's still a balance." },
          { text: "You get an email saying who paid and how much. To stop these, go to Settings, open Notifications and turn off Payment received." },
        ],
      },
    ],
  },
  {
    icon: Wallet,
    title: "Deposits & milestones",
    guides: [
      {
        title: "Taking a deposit",
        steps: [
          { text: "Open the quote or invoice, find the Payment Plan panel and choose Deposit." },
          { text: "Enter a percentage or a fixed amount, then click Save Deposit. It shows on the PDF." },
          { text: "On an invoice, QuoTrak creates a deposit invoice and a balance invoice, linked to the original. On a quote, it offers the deposit invoice when you mark the quote Accepted." },
        ],
      },
      {
        title: "Billing in milestones (Pro)",
        steps: [
          { text: "In the Payment Plan panel, choose Milestones." },
          { text: "Pick a Quick start split such as 30 / 40 / 30, or add your own stages. They need to total 100%." },
          { text: "Set milestones on a quote and they carry over when you convert it to an invoice." },
          { text: "Each stage gets its own invoice to send, and is marked paid separately." },
          { text: "A recurring invoice can't also use a payment plan." },
        ],
      },
    ],
  },
  {
    icon: RefreshCw,
    title: "Recurring (Pro)",
    guides: [
      {
        title: "Setting up a recurring invoice or quote",
        steps: [
          { text: "Open the invoice or quote, find the Recurring panel and switch it from Off to Recurring." },
          { text: "Choose how often: weekly, bi-weekly, monthly, quarterly, semi-annually, annually or custom." },
          { text: "Set a start date, and an end date if you want one." },
          { text: "This document becomes the template for the series. It isn't sent to the customer itself." },
        ],
      },
      {
        title: "Reviewing or auto-sending each copy",
        steps: [
          { text: "QuoTrak creates each copy on schedule, whether or not you're logged in." },
          { text: "Copies are drafts for you to review and send. Turn on Auto-send when generated to have invoices marked sent and emailed to the customer instead." },
          { text: "You get an email when copies are created. You can turn this off in Settings under Notifications." },
          { text: "When an invoice in the series has a payment link, your customer can choose to save their card for future invoices." },
        ],
      },
      {
        title: "Stopping a series",
        steps: [
          { text: "Open the original invoice or quote and set the Recurring panel back to Off, or set an end date." },
          { text: "If your Pro plan or trial ends, schedules pause and pick back up when you upgrade." },
        ],
      },
    ],
  },
  {
    icon: LayoutTemplate,
    title: "Templates (Pro)",
    guides: [
      {
        title: "Saving and using a template",
        steps: [
          { text: "Open a quote or invoice and click Save as Template at the top." },
          { text: "To use one, click From Template on the Quotes or Invoices list, or click Use on the Templates page." },
          { text: "The new document starts with the template's lines, groups and costs. Editing it doesn't change the template." },
          { text: "Rename, duplicate or delete templates from the Templates page." },
        ],
      },
    ],
  },
  {
    icon: Users,
    title: "Customers",
    guides: [
      {
        title: "Adding a customer",
        steps: [
          { text: "Click Customers in the sidebar, then New Customer, and fill in their details." },
          { text: "You can also add a customer while creating a quote or invoice, without leaving the page." },
        ],
      },
      {
        title: "Using a customer's profile",
        steps: [
          { text: "Click a customer to see their contact details, totals, and quote and invoice history." },
          { text: "Use New Quote or New Invoice on the profile to start one for that customer." },
          { text: "On Pro, use Attachments on the profile to keep images and PDFs with the customer." },
        ],
      },
    ],
  },
  {
    icon: Package,
    title: "Products & cost catalog",
    guides: [
      {
        title: "Adding products and services",
        steps: [
          { text: "Products & Services is what you sell, with a price. Go there from the sidebar and click New Product." },
          { text: "On a quote or invoice, click Link product on a line to fill it in." },
          { text: "When you add a new line, you can tick the box to save it as a product for next time." },
          { text: "On Pro, a product can carry default costs, so your margin fills in by itself." },
        ],
      },
      {
        title: "Starting your cost catalog",
        steps: [
          { text: "The Cost Catalog is what things cost you: labor, materials and fees. Customers never see it." },
          { text: "Use Import from your industry to load a starter list for your trade. This works on any plan." },
          { text: "The prices are starting points, so adjust them to match your market." },
          { text: "On Pro, click New Cost Item to add your own." },
        ],
      },
    ],
  },
  {
    icon: FolderKanban,
    title: "Projects & calendar (Pro)",
    guides: [
      {
        title: "Starting a project",
        steps: [
          { text: "Click Projects in the sidebar, then New Project, and choose the customer." },
          { text: "Attach quotes and invoices from the project's Billing tab, or from the Project line at the top of a quote or invoice." },
          { text: "Set the status as the job moves along, such as Lead, Quoted, Approved or In Progress." },
        ],
      },
      {
        title: "Tracking quoted against actual",
        steps: [
          { text: "Attaching a quote brings in its quoted materials. Add other costs by hand on the Costs tab." },
          { text: "Upload a receipt on the Costs tab, and QuoTrak reads the items for you to review." },
          { text: "Time your crew logs in Field is costed at each worker's rate." },
          { text: "The Overview tab compares quoted and actual cost, profit and margin, by labor, materials and fees." },
        ],
      },
      {
        title: "Handling change orders",
        steps: [
          { text: "Open the project's Change Orders tab to price extra work as it comes up." },
          { text: "The project shows what the change does to your totals." },
        ],
      },
      {
        title: "Exporting a project",
        steps: [
          { text: "Click Export at the top of the project to download a summary PDF or a ZIP archive of the job." },
        ],
      },
      {
        title: "Using the calendar",
        steps: [
          { text: "Calendar shows active projects by their start and target completion dates, as a Month or Timeline view. Dots mark quote expiries, invoice due dates and payments." },
          { text: "Click Export to subscribe from Google Calendar, Apple Calendar or Outlook, so your projects stay up to date there." },
        ],
      },
    ],
  },
  {
    icon: HardHat,
    title: "Field",
    guides: [
      {
        title: "Using Field Tools (every plan)",
        steps: [
          { text: "Click Field in the sidebar, then Field Tools." },
          { text: "Tools has the Unit Converter, Material Calculator, Wall Framer and Stair Stringer. Reference has trade charts, and Notes is for quick notes." },
          { text: "The calculators and charts are for reference only. Check your local code and the manufacturer's instructions before you build." },
        ],
      },
      {
        title: "Clocking in and out (Pro)",
        steps: [
          { text: "Open Field and pick a project. The nearest ones are listed first if you allow location." },
          { text: "Use Clock In when you start and Clock Out when you finish, with a note if you need one." },
          { text: "Account owners can use Add Entry to log time after the fact." },
        ],
      },
      {
        title: "Taking jobsite photos (Pro)",
        steps: [
          { text: "Open the project in Field and add your photos. They're tagged with your location if you allow it." },
          { text: "Add an area such as Kitchen or Roof, plus a note and tags, to keep them easy to find." },
        ],
      },
      {
        title: "Sharing a job with a customer (Pro)",
        steps: [
          { text: "Open the project in Field and click Share. A link to a photo summary is copied for you to send." },
          { text: "Your customer doesn't need to log in." },
          { text: "Export PDF on the same page downloads a report of the job." },
        ],
      },
      {
        title: "Inviting field workers (Pro)",
        steps: [
          { text: "Click Team in the sidebar, then Invite Field Worker." },
          { text: "Enter their name and email, and an hourly rate if you want their time costed. There's no extra charge per person." },
          { text: "They get an email with a link to set their password." },
          { text: "They sign in to the Field section to log time and take photos. They don't see your quotes, invoices, prices or customers." },
          { text: "Set your own rate on the same page under Your labor rate." },
        ],
      },
    ],
  },
  {
    icon: Settings,
    title: "Settings",
    guides: [
      {
        title: "Adding your logo and color (Pro)",
        steps: [
          { text: "Go to Settings and open Branding." },
          { text: "Click Upload Logo and choose a PNG or JPG under 2 MB. It appears at the top left of your PDFs." },
          { text: "Choose an Accent Color to tint the headings and totals on your customer PDFs." },
          { text: "Free accounts created before November 2026 keep their logo and color." },
        ],
      },
      {
        title: "Changing how your PDFs look",
        steps: [
          { text: "Open Quote Template or Invoice Template. Each one controls its own customer PDF." },
          { text: "Set the Document Title, such as QUOTE or PROPOSAL, and choose which columns print and what they're called." },
          { text: "Add your standard Notes / Terms. On Pro, you can also change the PDF Footer." },
          { text: "Use Preview customer PDF to check it with sample data." },
        ],
      },
      {
        title: "Setting taxes",
        steps: [
          { text: "Open Taxes and click Add Tax Line." },
          { text: "Enter a name and rate. You can add as many as you need, and they work for both quotes and invoices." },
        ],
      },
      {
        title: "Setting your emails (Pro)",
        steps: [
          { text: "Open Email Defaults to pre-fill the subject and body of quote and invoice emails." },
          { text: "Open Additional Email Attachments to upload a service agreement that goes out with your quote emails." },
          { text: "In Business Information, set a Reply-to Email so customer replies come to you." },
        ],
      },
      {
        title: "Choosing your notifications",
        steps: [
          { text: "Open Notifications to turn emails on or off: overdue invoices, to-do reminders, recurring documents and payments received." },
          { text: "Set the hour your daily reminders go out. It uses your timezone from Business Information." },
        ],
      },
      {
        title: "Changing your email or password",
        steps: [
          { text: "Open Account to update your name, email or password. A new email address needs to be verified first." },
        ],
      },
    ],
  },
  {
    icon: Plug,
    title: "Integrations (Pro)",
    guides: [
      {
        title: "QuickBooks Online and Xero",
        steps: [
          { text: "Go to Integrations in the sidebar and connect your account." },
          { text: "Use the QuickBooks or Xero button on a quote or invoice, or tick several on the list and send them together." },
          { text: "The first send creates the document and later sends update it. Nothing happens until you send, and nothing comes back into QuoTrak." },
        ],
      },
      {
        title: "Zapier",
        steps: [
          { text: "Go to Integrations and click Set up beside Zapier." },
          { text: "In Zapier, create a Zap with the Webhooks by Zapier Catch Hook trigger, and paste its URL into QuoTrak." },
          { text: "Zaps can run when a quote is sent or accepted, an invoice is sent or paid, or a customer is created." },
        ],
      },
      {
        title: "Google Sheets",
        steps: [
          { text: "Go to Integrations and connect your Google account beside Google Sheets." },
          { text: "Choose the sheet you want to use." },
          { text: "Click Sync Now whenever you want to update it. It doesn't update by itself." },
        ],
      },
    ],
  },
  {
    icon: Download,
    title: "Import & export (Pro)",
    guides: [
      {
        title: "Importing from a CSV",
        steps: [
          { text: "Go to Import & Export in the sidebar and open Import." },
          { text: "Choose Customers, Products & Services or Cost Catalog, and download the template to see the columns you need." },
          { text: "Fill it in, upload it and check the results." },
        ],
      },
      {
        title: "Importing an invoice from a PDF",
        steps: [
          { text: "On the Import tab, use Import from PDF and upload the invoice." },
          { text: "QuoTrak reads the customer, lines and totals. Check them over and correct anything before you save it." },
        ],
      },
      {
        title: "Exporting your data",
        steps: [
          { text: "Open Export to download invoices as a file for QuickBooks." },
          { text: "Use Spreadsheet (CSV) Export to download quotes or invoices for Excel or Numbers." },
          { text: "Filter by status and date before you download." },
        ],
      },
    ],
  },
  {
    icon: BarChart2,
    title: "Dashboard",
    guides: [
      {
        title: "Reading your dashboard",
        steps: [
          { text: "The top cards show Total Value, Accepted Value, Accepted Profit, Pending Value, Acceptance Rate and Average Margin." },
          { text: "Use the date buttons to switch between All Time, the past 7, 30 or 90 days, or a custom range." },
          { text: "Below that are your quotes by status, margin per quote, invoice totals, revenue by service and outstanding invoices." },
        ],
      },
      {
        title: "Using the to-do list",
        steps: [
          { text: "Type a task in To-Do on the dashboard, add a due date if you want one, and click Add task." },
          { text: "QuoTrak can email you before a due date. Set this in Settings under Notifications." },
        ],
      },
      {
        title: "Checking your activity log",
        steps: [
          { text: "Click Activity in the sidebar to see recent emails, payments and automated actions from the last 90 days." },
        ],
      },
    ],
  },
];

type Tab = "guides" | "support" | "ask-ai" | "feature-requests" | "setup-guide" | "referrals" | "whats-new";

type ChatMessage = {
  role: "user" | "assistant";
  content: string;
};

type SupportTicket = {
  id: number;
  ticketRef: string;
  subject: string;
  status: string;
  createdAt: string;
};

type TicketMessage = {
  id: number;
  isAdmin: boolean;
  body: string;
  createdAt: string;
};

type TicketDetail = {
  id: number;
  ticketRef: string;
  subject: string;
  message: string;
  status: string;
  createdAt: string;
  messages: TicketMessage[];
};

type FeatureRequest = {
  id: number;
  title: string;
  description: string;
  upvotes: number;
  status: string;
  createdAt: string;
  votedByMe: boolean;
};

const BASE_URL = import.meta.env.BASE_URL.replace(/\/$/, "");

function statusBadgeVariant(status: string): "default" | "secondary" | "outline" {
  if (status === "open") return "default";
  if (status === "in_progress") return "secondary";
  return "outline";
}

function statusLabel(status: string): string {
  if (status === "open") return "Open";
  if (status === "in_progress") return "In Progress";
  if (status === "resolved") return "Resolved";
  return status;
}

function openGuidePopup(guide: Guide) {
  const stepsHtml = guide.steps
    .map(
      (step, i) =>
        `<li style="display:flex;align-items:flex-start;gap:0.75rem;margin-bottom:0.75rem;">
          <span style="flex-shrink:0;width:1.375rem;height:1.375rem;border-radius:50%;background:#e8f0fe;color:#2563eb;font-size:0.7rem;font-weight:600;display:flex;align-items:center;justify-content:center;margin-top:0.125rem;">${i + 1}</span>
          <span style="font-size:0.875rem;color:#4b5563;line-height:1.4;">${step.text.replace(/</g, "&lt;").replace(/>/g, "&gt;")}</span>
        </li>`
    )
    .join("");

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${guide.title.replace(/</g, "&lt;")}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: system-ui, -apple-system, sans-serif; background: #f9fafb; color: #111827; padding: 1.5rem; }
    h1 { font-size: 1rem; font-weight: 600; margin-bottom: 1.25rem; color: #111827; }
    ol { list-style: none; padding: 0; }
  </style>
</head>
<body>
  <h1>${guide.title.replace(/</g, "&lt;")}</h1>
  <ol>${stepsHtml}</ol>
</body>
</html>`;

  const popup = window.open("", "_blank", "width=480,height=640,resizable=no,scrollbars=yes,toolbar=no,menubar=no,location=no,status=no");
  if (popup) {
    popup.document.write(html);
    popup.document.close();
  }
}

function GuideAccordionItem({ guide, isOpen, onToggle }: { guide: { title: string; steps: { text: string }[] }; isOpen: boolean; onToggle: () => void }) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      setHeight(isOpen ? contentRef.current.scrollHeight : 0);
    }
  }, [isOpen]);

  return (
    <li>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-2.5 text-sm text-left hover:text-primary transition-colors group"
      >
        <span className={isOpen ? "text-primary font-medium" : "text-muted-foreground group-hover:text-primary transition-colors"}>
          {guide.title}
        </span>
        <ChevronDown
          className="h-3.5 w-3.5 flex-shrink-0 transition-all duration-200"
          style={{
            transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)",
            color: isOpen ? "hsl(var(--primary))" : "hsl(var(--muted-foreground) / 0.5)",
          }}
        />
      </button>
      <div
        aria-hidden={!isOpen}
        style={{
          height: `${height}px`,
          overflow: "hidden",
          transition: "height 200ms ease, opacity 200ms ease",
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? "auto" : "none",
        }}
      >
        <div ref={contentRef} className="pb-3">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-muted-foreground">{guide.steps.length} steps</span>
            <button
              onClick={() => openGuidePopup(guide)}
              tabIndex={isOpen ? 0 : -1}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
              title="Open in window"
            >
              <ExternalLink className="h-3 w-3" />
              Open in window
            </button>
          </div>
          <ol className="space-y-3">
            {guide.steps.map((step, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-muted-foreground leading-snug">
                <span
                  style={{
                    flexShrink: 0,
                    width: "1.375rem",
                    height: "1.375rem",
                    borderRadius: "50%",
                    background: "hsl(var(--primary) / 0.1)",
                    color: "hsl(var(--primary))",
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginTop: "0.125rem",
                  }}
                >
                  {i + 1}
                </span>
                <span>{step.text}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </li>
  );
}

function GuidesTab() {
  const [expandedGuide, setExpandedGuide] = useState<string | null>(null);

  function toggleGuide(title: string) {
    setExpandedGuide((prev) => (prev === title ? null : title));
  }

  return (
    <div className="space-y-3">
      {guideCategories.map(({ icon: Icon, title, guides }) => (
        <Card key={title} className="border-border/50 shadow-sm">
          <CardHeader className="pb-2 pt-4">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Icon className="h-4 w-4 text-primary" />
              {title}
              <Badge variant="secondary" className="ml-auto text-xs font-normal">
                {guides.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-2 pt-0">
            <ul className="divide-y divide-border/40">
              {guides.map((guide) => (
                <GuideAccordionItem
                  key={guide.title}
                  guide={guide}
                  isOpen={expandedGuide === guide.title}
                  onToggle={() => toggleGuide(guide.title)}
                />
              ))}
            </ul>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function TicketThread({ ticket, onBack, onMessageSent }: { ticket: TicketDetail; onBack: () => void; onMessageSent: (msg: TicketMessage) => void }) {
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const { toast } = useToast();
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [ticket.messages]);

  const handleSend = async () => {
    const trimmed = replyText.trim();
    if (!trimmed) return;
    setSending(true);
    try {
      const res = await fetch(`${BASE_URL}/api/support/tickets/${ticket.id}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ body: trimmed }),
      });
      if (!res.ok) throw new Error("Failed");
      const msg: TicketMessage = await res.json();
      onMessageSent(msg);
      setReplyText("");
    } catch {
      toast({ title: "Error", description: "Failed to send reply. Please try again.", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const allMessages: { isAdmin: boolean; body: string; createdAt: string; isOriginal?: boolean }[] = [
    { isAdmin: false, body: ticket.message, createdAt: ticket.createdAt, isOriginal: true },
    ...ticket.messages,
  ];

  return (
    <div className="max-w-2xl">
      <button
        onClick={onBack}
        style={{ display: "flex", alignItems: "center", gap: "0.375rem", color: "hsl(var(--muted-foreground))", fontSize: "0.875rem", background: "none", border: "none", cursor: "pointer", marginBottom: "1rem", padding: 0 }}
      >
        <ArrowLeft style={{ width: "1rem", height: "1rem" }} />
        Back to tickets
      </button>

      <div style={{ marginBottom: "1rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.25rem" }}>
          <h2 style={{ fontSize: "1.125rem", fontWeight: 700, margin: 0 }}>{ticket.subject}</h2>
          <Badge variant={statusBadgeVariant(ticket.status)} className="text-xs">{statusLabel(ticket.status)}</Badge>
        </div>
        <p style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))", margin: 0 }}>
          {ticket.ticketRef} · {new Date(ticket.createdAt).toLocaleDateString()}
        </p>
      </div>

      <div style={{ border: "1px solid hsl(var(--border))", borderRadius: "0.5rem", overflow: "hidden", marginBottom: "1rem" }}>
        <div style={{ maxHeight: "28rem", overflowY: "auto", padding: "1rem", display: "flex", flexDirection: "column", gap: "0.875rem" }}>
          {allMessages.map((msg, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                justifyContent: msg.isAdmin ? "flex-start" : "flex-end",
              }}
            >
              <div style={{ maxWidth: "80%" }}>
                {msg.isAdmin && (
                  <p style={{ fontSize: "0.6875rem", color: "hsl(var(--muted-foreground))", marginBottom: "0.25rem", fontWeight: 600 }}>
                    Support Team
                  </p>
                )}
                <div
                  style={{
                    padding: "0.625rem 0.875rem",
                    borderRadius: msg.isAdmin ? "0.75rem 0.75rem 0.75rem 0.25rem" : "0.75rem 0.75rem 0.25rem 0.75rem",
                    background: msg.isAdmin ? "hsl(var(--muted))" : "hsl(var(--primary))",
                    color: msg.isAdmin ? "hsl(var(--foreground))" : "hsl(var(--primary-foreground))",
                    fontSize: "0.875rem",
                    lineHeight: "1.5",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {msg.body}
                </div>
                <p style={{ fontSize: "0.6875rem", color: "hsl(var(--muted-foreground))", marginTop: "0.25rem", textAlign: msg.isAdmin ? "left" : "right" }}>
                  {new Date(msg.createdAt).toLocaleString()}
                  {msg.isOriginal ? " (original message)" : ""}
                </p>
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
      </div>

      {ticket.status !== "resolved" ? (
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <Textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Type your reply..."
            rows={3}
            style={{ flex: 1, resize: "vertical" }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
          <Button onClick={handleSend} disabled={sending || !replyText.trim()} style={{ alignSelf: "flex-end" }}>
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      ) : (
        <p style={{ fontSize: "0.875rem", color: "hsl(var(--muted-foreground))", textAlign: "center", padding: "0.75rem", background: "hsl(var(--muted) / 0.5)", borderRadius: "0.5rem" }}>
          This ticket has been resolved. Open a new ticket if you need further assistance.
        </p>
      )}
    </div>
  );
}

function SupportTab() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { unreadByTicket, markTicketRead, refresh: refreshNotifications } = useSupportNotifications();
  const [name, setName] = useState(user?.name ?? "");
  const [email, setEmail] = useState(user?.email ?? "");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<{ ticketRef: string } | null>(null);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState<TicketDetail | null>(null);
  const [loadingTicket, setLoadingTicket] = useState(false);

  useEffect(() => {
    if (!user) {
      setLoadingTickets(false);
      return;
    }
    fetch(`${BASE_URL}/api/support/tickets`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) setTickets(data);
      })
      .catch(() => {})
      .finally(() => setLoadingTickets(false));
  }, [user]);

  const deepLinkOpenedRef = useRef(false);
  useEffect(() => {
    if (!user || deepLinkOpenedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const ticketIdStr = params.get("ticketId");
    if (!ticketIdStr) return;
    const ticketId = parseInt(ticketIdStr, 10);
    if (isNaN(ticketId)) return;
    deepLinkOpenedRef.current = true;
    openTicket(ticketId);
  }, [user]);

  const openTicket = async (ticketId: number) => {
    setLoadingTicket(true);
    try {
      const res = await fetch(`${BASE_URL}/api/support/tickets/${ticketId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      const data: TicketDetail = await res.json();
      setSelectedTicket(data);
      await markTicketRead(ticketId);
      refreshNotifications();
    } catch {
      toast({ title: "Error", description: "Failed to load ticket.", variant: "destructive" });
    } finally {
      setLoadingTicket(false);
    }
  };

  const handleBack = () => {
    setSelectedTicket(null);
  };

  const handleMessageSent = (msg: TicketMessage) => {
    if (selectedTicket) {
      setSelectedTicket({ ...selectedTicket, messages: [...selectedTicket.messages, msg] });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${BASE_URL}/api/support/tickets`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name, email, subject, message }),
      });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      setSubmitted({ ticketRef: data.ticketRef });
      setTickets((prev) => [
        { id: data.id, ticketRef: data.ticketRef, subject, status: "open", createdAt: data.createdAt },
        ...prev,
      ]);
      setSubject("");
      setMessage("");
    } catch {
      toast({ title: "Error", description: "Failed to submit ticket. Please try again.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  if (selectedTicket) {
    return (
      <TicketThread
        ticket={selectedTicket}
        onBack={handleBack}
        onMessageSent={handleMessageSent}
      />
    );
  }

  return (
    <div className="max-w-lg space-y-8">
      {submitted ? (
        <div
          style={{
            padding: "1.25rem",
            borderRadius: "0.75rem",
            background: "hsl(var(--primary) / 0.06)",
            border: "1px solid hsl(var(--primary) / 0.2)",
          }}
        >
          <p className="font-semibold text-foreground">Message sent!</p>
          <p className="text-sm text-muted-foreground mt-1">
            Your support ticket has been created. Reference number:{" "}
            <span className="font-mono font-semibold text-foreground">{submitted.ticketRef}</span>
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => setSubmitted(null)}
          >
            Send another message
          </Button>
        </div>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            Have a question or issue? Send us a message and we'll get back to you as soon as possible.
          </p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="contact-subject">Subject</Label>
              <Input
                id="contact-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Brief summary of your issue"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-message">Message</Label>
              <Textarea
                id="contact-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Describe your question or issue..."
                rows={5}
                required
              />
            </div>
            <Button type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Message
                </>
              )}
            </Button>
          </form>
        </>
      )}

      {user && (
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3">Your past tickets</h3>
          {loadingTickets ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Loading...
            </div>
          ) : tickets.length === 0 ? (
            <p className="text-sm text-muted-foreground">No tickets yet.</p>
          ) : (
            <div className="border border-border/50 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: "hsl(var(--muted))" }}>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Ref</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Subject</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Status</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">Date</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground"></th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((t, i) => {
                    const ticketUnread = unreadByTicket[t.id] ?? 0;
                    return (
                      <tr
                        key={t.id}
                        style={{
                          borderTop: i > 0 ? "1px solid hsl(var(--border))" : undefined,
                          background: i % 2 === 0 ? "hsl(var(--card))" : "hsl(var(--muted) / 0.3)",
                          cursor: "pointer",
                        }}
                        onClick={() => openTicket(t.id)}
                      >
                        <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{t.ticketRef}</td>
                        <td className="px-3 py-2 text-foreground max-w-[180px]">
                          <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.subject}</span>
                            {ticketUnread > 0 && (
                              <span style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minWidth: "1.125rem",
                                height: "1.125rem",
                                padding: "0 0.2rem",
                                borderRadius: "9999px",
                                background: "#ef4444",
                                color: "#fff",
                                fontSize: "0.625rem",
                                fontWeight: 700,
                                flexShrink: 0,
                              }}>
                                {ticketUnread}
                              </span>
                            )}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <Badge variant={statusBadgeVariant(t.status)} className="text-xs">
                            {statusLabel(t.status)}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-muted-foreground text-xs">
                          {new Date(t.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-3 py-2">
                          {loadingTicket ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FeatureRequestsTab() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [requests, setRequests] = useState<FeatureRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [votingId, setVotingId] = useState<number | null>(null);

  const fetchRequests = () => {
    fetch(`${BASE_URL}/api/support/feature-requests`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) setRequests(data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      toast({ title: "Please sign in", description: "You must be signed in to submit a feature request." });
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${BASE_URL}/api/support/feature-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ title, description }),
      });
      if (!res.ok) throw new Error("Failed");
      setTitle("");
      setDescription("");
      setShowForm(false);
      toast({ title: "Feature request submitted!", description: "Thanks for your suggestion." });
      fetchRequests();
    } catch {
      toast({ title: "Error", description: "Failed to submit. Please try again.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleVote = async (fr: FeatureRequest) => {
    if (!user) {
      toast({ title: "Please sign in", description: "You must be signed in to vote." });
      return;
    }
    if (votingId === fr.id) return;
    setVotingId(fr.id);

    const method = fr.votedByMe ? "DELETE" : "POST";
    try {
      const res = await fetch(`${BASE_URL}/api/support/feature-requests/${fr.id}/vote`, {
        method,
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      setRequests((prev) =>
        prev
          .map((r) => (r.id === fr.id ? { ...r, upvotes: data.upvotes, votedByMe: data.votedByMe } : r))
          .sort((a, b) => b.upvotes - a.upvotes || new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      );
    } catch {
      toast({ title: "Error", description: "Failed to update vote.", variant: "destructive" });
    } finally {
      setVotingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">
            Vote for features you'd like to see, or suggest your own ideas.
          </p>
        </div>
        <Button
          variant={showForm ? "outline" : "default"}
          size="sm"
          onClick={() => setShowForm((v) => !v)}
        >
          {showForm ? (
            <>
              <ChevronUp className="h-4 w-4 mr-1.5" />
              Cancel
            </>
          ) : (
            <>
              <Lightbulb className="h-4 w-4 mr-1.5" />
              Suggest a Feature
            </>
          )}
        </Button>
      </div>

      {showForm && (
        <Card className="border-primary/20 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">New Feature Request</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="fr-title">Title</Label>
                <Input
                  id="fr-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Short, descriptive title"
                  required
                  maxLength={200}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="fr-description">Description</Label>
                <Textarea
                  id="fr-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe the feature and why it would be useful..."
                  rows={4}
                  required
                  maxLength={2000}
                />
              </div>
              <Button type="submit" size="sm" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="h-3.5 w-3.5 mr-1.5" />
                    Submit Request
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading feature requests...
        </div>
      ) : requests.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Lightbulb className="h-8 w-8 mx-auto mb-3 opacity-30" />
          <p className="text-sm font-medium">No feature requests yet</p>
          <p className="text-xs mt-1 opacity-70">Be the first to suggest an idea!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((fr) => (
            <Card key={fr.id} className="border-border/50 shadow-sm">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-start gap-4">
                  <div className="flex flex-col items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => handleVote(fr)}
                      disabled={votingId === fr.id}
                      aria-label={fr.votedByMe ? "Unlike feature request" : "Like feature request"}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "0.5rem",
                        borderRadius: "0.5rem",
                        border: fr.votedByMe
                          ? "1px solid hsl(var(--primary))"
                          : "1px solid hsl(var(--border))",
                        background: fr.votedByMe ? "hsl(var(--primary) / 0.08)" : "transparent",
                        cursor: votingId === fr.id ? "wait" : "pointer",
                        color: fr.votedByMe ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))",
                        transition: "all 0.15s",
                      }}
                    >
                      {votingId === fr.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <ThumbsUp className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-sm text-foreground leading-snug">{fr.title}</p>
                      {fr.status === "implemented" && (
                        <span style={{
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "0.125rem 0.5rem",
                          borderRadius: "9999px",
                          fontSize: "0.6875rem",
                          fontWeight: 500,
                          background: "#10b98120",
                          color: "#10b981",
                          border: "1px solid #10b98140",
                        }}>
                          Implemented
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1 leading-relaxed line-clamp-2">
                      {fr.description}
                    </p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {new Date(fr.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

type ChatSession = {
  id: number;
  title: string;
  createdAt: string;
  updatedAt: string;
};

function formatSessionDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function AskAITab() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [view, setView] = useState<"chat" | "history">("chat");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  const loadSessions = async () => {
    try {
      setSessionsLoading(true);
      const res = await fetch(`${BASE_URL}/api/support/chat/sessions`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json() as ChatSession[];
        setSessions(data);
      }
    } catch {
    } finally {
      setSessionsLoading(false);
    }
  };

  useEffect(() => {
    loadSessions();
  }, []);

  const openSession = async (sid: number) => {
    try {
      const res = await fetch(`${BASE_URL}/api/support/chat/sessions/${sid}`, { credentials: "include" });
      if (!res.ok) return;
      const data = await res.json() as { messages: ChatMessage[] };
      setMessages(Array.isArray(data.messages) ? data.messages : []);
      setSessionId(sid);
      setView("chat");
    } catch {
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setSessionId(null);
    setInput("");
    setView("chat");
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || isTyping) return;

    const userMessage: ChatMessage = { role: "user", content: text };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput("");
    setIsTyping(true);

    try {
      const payload = sessionId
        ? { messages: [userMessage], sessionId }
        : { messages: updatedMessages };

      const response = await fetch(`${BASE_URL}/api/support/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("Request failed");

      const reader = response.body?.getReader();
      if (!reader) throw new Error("No response body");

      const decoder = new TextDecoder();
      let assistantContent = "";
      let lineBuffer = "";
      let firstToken = true;
      let newSessionId = sessionId;

      const processLine = (line: string) => {
        if (!line.startsWith("data: ")) return false;
        const data = line.slice(6).trim();
        if (!data) return false;
        try {
          const parsed = JSON.parse(data) as { content?: string; done?: boolean; error?: string; sessionId?: number };
          if (parsed.content) {
            if (firstToken) {
              firstToken = false;
              setIsTyping(false);
              setMessages((prev) => [...prev, { role: "assistant", content: parsed.content! }]);
              assistantContent = parsed.content!;
            } else {
              assistantContent += parsed.content;
              setMessages((prev) => {
                const next = [...prev];
                next[next.length - 1] = { role: "assistant", content: assistantContent };
                return next;
              });
            }
          }
          if (parsed.sessionId) {
            newSessionId = parsed.sessionId;
          }
          if (parsed.done || parsed.error) return true;
        } catch {
        }
        return false;
      };

      outer: while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        lineBuffer += decoder.decode(value, { stream: true });
        const lines = lineBuffer.split("\n");
        lineBuffer = lines.pop() ?? "";

        for (const line of lines) {
          if (processLine(line)) break outer;
        }
      }
      if (firstToken) {
        setIsTyping(false);
      }

      if (newSessionId && newSessionId !== sessionId) {
        setSessionId(newSessionId);
      }

      loadSessions();
    } catch (err) {
      setIsTyping(false);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Sorry, I ran into an issue. Please try again." },
      ]);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (view === "history") {
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "28rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.75rem" }}>
          <Button variant="ghost" size="sm" onClick={() => setView("chat")} style={{ padding: "0.25rem 0.5rem" }}>
            <ArrowLeft style={{ width: "1rem", height: "1rem", marginRight: "0.25rem" }} />
            Back
          </Button>
          <span style={{ fontSize: "0.9375rem", fontWeight: 600, color: "hsl(var(--foreground))" }}>Recent Chats</span>
        </div>
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: "0.375rem",
          }}
        >
          {sessionsLoading ? (
            <div style={{ margin: "auto", color: "hsl(var(--muted-foreground))", fontSize: "0.875rem" }}>
              Loading…
            </div>
          ) : sessions.length === 0 ? (
            <div style={{ margin: "auto", textAlign: "center", color: "hsl(var(--muted-foreground))" }}>
              <MessageSquare style={{ width: "2rem", height: "2rem", margin: "0 auto 0.5rem", opacity: 0.4 }} />
              <p style={{ fontSize: "0.875rem" }}>No chat history yet</p>
            </div>
          ) : (
            sessions.map((s) => (
              <button
                key={s.id}
                onClick={() => openSession(s.id)}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: "0.125rem",
                  padding: "0.625rem 0.75rem",
                  borderRadius: "0.5rem",
                  border: "1px solid hsl(var(--border))",
                  background: sessionId === s.id ? "hsl(var(--accent))" : "hsl(var(--card))",
                  cursor: "pointer",
                  textAlign: "left",
                  width: "100%",
                  transition: "background 0.15s",
                }}
              >
                <span style={{ fontSize: "0.875rem", fontWeight: 500, color: "hsl(var(--foreground))", lineHeight: 1.4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}>
                  {s.title}
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: "0.25rem", fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>
                  <Clock style={{ width: "0.75rem", height: "0.75rem" }} />
                  {formatSessionDate(s.updatedAt)}
                </span>
              </button>
            ))
          )}
        </div>
        <div style={{ marginTop: "0.75rem" }}>
          <Button onClick={startNewChat} style={{ width: "100%" }}>
            New Chat
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "28rem" }}>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "0.5rem", gap: "0.5rem" }}>
        <Button variant="outline" size="sm" onClick={() => { loadSessions(); setView("history"); }} style={{ fontSize: "0.8125rem" }}>
          <Clock style={{ width: "0.875rem", height: "0.875rem", marginRight: "0.375rem" }} />
          Recent chats
        </Button>
        {messages.length > 0 && (
          <Button variant="outline" size="sm" onClick={startNewChat} style={{ fontSize: "0.8125rem" }}>
            New chat
          </Button>
        )}
      </div>

      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "1rem",
          borderRadius: "0.5rem",
          border: "1px solid hsl(var(--border))",
          background: "hsl(var(--card))",
          marginBottom: "0.75rem",
          display: "flex",
          flexDirection: "column",
          gap: "0.75rem",
        }}
      >
        {messages.length === 0 && !sessionsLoading && sessions.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.375rem", width: "100%" }}>
            <p style={{ fontSize: "0.8125rem", fontWeight: 600, color: "hsl(var(--muted-foreground))", marginBottom: "0.125rem" }}>
              Recent chats
            </p>
            {sessions.map((s) => (
              <button
                key={s.id}
                onClick={() => openSession(s.id)}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: "0.125rem",
                  padding: "0.625rem 0.75rem",
                  borderRadius: "0.5rem",
                  border: "1px solid hsl(var(--border))",
                  background: "hsl(var(--card))",
                  cursor: "pointer",
                  textAlign: "left",
                  width: "100%",
                  transition: "background 0.15s",
                }}
              >
                <span style={{ fontSize: "0.875rem", fontWeight: 500, color: "hsl(var(--foreground))", lineHeight: 1.4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}>
                  {s.title}
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: "0.25rem", fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>
                  <Clock style={{ width: "0.75rem", height: "0.75rem" }} />
                  {formatSessionDate(s.updatedAt)}
                </span>
              </button>
            ))}
            <div style={{ marginTop: "0.5rem", textAlign: "center", color: "hsl(var(--muted-foreground))", fontSize: "0.8125rem" }}>
              — or ask a new question below —
            </div>
          </div>
        )}

        {messages.length === 0 && !sessionsLoading && sessions.length === 0 && (
          <div style={{ margin: "auto", textAlign: "center", color: "hsl(var(--muted-foreground))" }}>
            <Bot style={{ width: "2.5rem", height: "2.5rem", margin: "0 auto 0.75rem", opacity: 0.4 }} />
            <p style={{ fontSize: "0.875rem", fontWeight: 500 }}>Ask anything about QuoTrak</p>
            <p style={{ fontSize: "0.75rem", marginTop: "0.25rem", opacity: 0.7 }}>
              Get help with quotes, invoices, customers, catalogs, and more.
            </p>
          </div>
        )}

        {messages.length === 0 && sessionsLoading && (
          <div style={{ margin: "auto", textAlign: "center", color: "hsl(var(--muted-foreground))" }}>
            <Bot style={{ width: "2.5rem", height: "2.5rem", margin: "0 auto 0.75rem", opacity: 0.4 }} />
            <p style={{ fontSize: "0.875rem", fontWeight: 500 }}>Ask anything about QuoTrak</p>
            <p style={{ fontSize: "0.75rem", marginTop: "0.25rem", opacity: 0.7 }}>
              Get help with quotes, invoices, customers, catalogs, and more.
            </p>
          </div>
        )}

        {messages.map((msg, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
            }}
          >
            <div
              style={{
                maxWidth: "85%",
                padding: "0.625rem 0.875rem",
                borderRadius: msg.role === "user" ? "1rem 1rem 0.25rem 1rem" : "1rem 1rem 1rem 0.25rem",
                background: msg.role === "user" ? "hsl(var(--primary))" : "hsl(var(--muted))",
                color: msg.role === "user" ? "hsl(var(--primary-foreground))" : "hsl(var(--foreground))",
                fontSize: "0.875rem",
                lineHeight: "1.5",
                whiteSpace: "pre-wrap",
              }}
            >
              {msg.content}
            </div>
          </div>
        ))}

        {isTyping && (
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <div
              style={{
                padding: "0.625rem 0.875rem",
                borderRadius: "1rem 1rem 1rem 0.25rem",
                background: "hsl(var(--muted))",
                display: "flex",
                alignItems: "center",
                gap: "0.25rem",
              }}
            >
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  style={{
                    width: "0.4rem",
                    height: "0.4rem",
                    borderRadius: "50%",
                    background: "hsl(var(--muted-foreground))",
                    display: "inline-block",
                    animation: "bounce 1.2s infinite",
                    animationDelay: `${i * 0.2}s`,
                  }}
                />
              ))}
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <style>{`
        @keyframes bounce {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
          40% { transform: translateY(-0.4rem); opacity: 1; }
        }
      `}</style>

      <div style={{ display: "flex", gap: "0.5rem" }}>
        <Input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask a question about QuoTrak..."
          disabled={isTyping}
          style={{ flex: 1 }}
        />
        <Button onClick={sendMessage} disabled={!input.trim() || isTyping} size="icon">
          <Send className="h-4 w-4" />
          <span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0,0,0,0)" }}>
            Send
          </span>
        </Button>
      </div>
    </div>
  );
}

function SetupGuideTab() {
  const { guideState } = useSetupGuide();

  if (!guideState) {
    return (
      <p className="text-sm text-muted-foreground">Setup guide is not available.</p>
    );
  }

  const { completedSteps, dismissedSteps, steps, plan } = guideState;
  const resolvedDismissed = dismissedSteps ?? [];
  const completedCount = steps.filter(s => completedSteps.includes(s) || resolvedDismissed.includes(s)).length;
  const totalCount = steps.length;
  const progressPct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  const allHandled = steps.every(s => completedSteps.includes(s) || resolvedDismissed.includes(s));

  return (
    <div style={{ maxWidth: "30rem" }}>
      <div style={{ marginBottom: "1rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.25rem" }}>
          <Sparkles style={{ width: "1.125rem", height: "1.125rem", color: "hsl(var(--primary))" }} />
          <h2 style={{ fontSize: "1.125rem", fontWeight: 700, color: "hsl(var(--foreground))", margin: 0 }}>
            {plan === "pro" ? "Pro Setup Guide" : "Setup Guide"}
          </h2>
        </div>
        <p style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))", margin: 0 }}>
          {allHandled ? "You've handled all setup steps." : "Complete these steps to get the most out of QuoTrak."}
        </p>
      </div>

      <div style={{ marginBottom: "1rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.375rem" }}>
          <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "hsl(var(--foreground))" }}>
            {completedCount} of {totalCount} complete
          </span>
          <span style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>{progressPct}%</span>
        </div>
        <div style={{ height: "0.5rem", borderRadius: "9999px", background: "hsl(var(--muted))", overflow: "hidden" }}>
          <div style={{
            height: "100%",
            borderRadius: "9999px",
            background: allHandled ? "hsl(142, 71%, 45%)" : "hsl(var(--primary))",
            width: `${progressPct}%`,
            transition: "width 0.4s ease",
          }} />
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "0.375rem" }}>
        {steps.map((stepId) => {
          const config = CHECKLIST_ITEMS[stepId];
          const isComplete = completedSteps.includes(stepId);
          const isDismissed = resolvedDismissed.includes(stepId);
          const isHandled = isComplete || isDismissed;
          const Icon = config.icon;
          return (
            <div
              key={stepId}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                padding: "0.75rem",
                borderRadius: "0.5rem",
                border: `1px solid ${isHandled ? "hsl(var(--border) / 0.5)" : "hsl(var(--border))"}`,
                background: isComplete
                  ? "hsl(142 71% 45% / 0.06)"
                  : isDismissed
                  ? "hsl(var(--muted) / 0.3)"
                  : "hsl(var(--card))",
              }}
            >
              {isComplete
                ? <CheckCircle2 style={{ width: "1.25rem", height: "1.25rem", color: "hsl(142, 71%, 45%)", flexShrink: 0 }} />
                : <Icon style={{ width: "1.25rem", height: "1.25rem", flexShrink: 0, color: isDismissed ? "hsl(var(--muted-foreground) / 0.5)" : "hsl(var(--muted-foreground))" }} />
              }
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  color: isComplete ? "hsl(var(--muted-foreground))" : isDismissed ? "hsl(var(--muted-foreground) / 0.6)" : "hsl(var(--foreground))",
                  textDecoration: isDismissed ? "line-through" : "none",
                  marginBottom: isHandled ? 0 : "0.125rem",
                }}>
                  {config.title}
                </div>
                {!isHandled && (
                  <div style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>{config.description}</div>
                )}
              </div>
              {isHandled && isComplete && (
                <Check style={{ width: "1rem", height: "1rem", color: "hsl(142, 71%, 45%)", flexShrink: 0 }} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

type ReferralEntry = {
  id: number;
  name: string | null;
  email: string | null;
  status: "pending" | "rewarded" | "invited";
  createdAt: string;
  rewardedAt: string | null;
  type: "signup" | "invite";
};

function ReferralsTab() {
  const [referralLink, setReferralLink] = useState<string | null>(null);
  const [referrals, setReferrals] = useState<ReferralEntry[]>([]);
  const [totalRewarded, setTotalRewarded] = useState(0);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const { toast } = useToast();

  const loadData = () => {
    return Promise.all([
      fetch(`${BASE_URL}/api/referrals/me`, { credentials: "include" }).then((r) => r.json()),
      fetch(`${BASE_URL}/api/referrals`, { credentials: "include" }).then((r) => r.json()),
    ])
      .then(([meData, listData]) => {
        if (meData?.link) setReferralLink(meData.link);
        if (Array.isArray(listData?.referrals)) setReferrals(listData.referrals);
        if (typeof listData?.totalRewarded === "number") setTotalRewarded(listData.totalRewarded);
      })
      .catch(() => {
        toast({ title: "Failed to load referral info", variant: "destructive" });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, []);

  const handleSendInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviteSending(true);
    setInviteError(null);
    setInviteSuccess(false);
    try {
      const res = await fetch(`${BASE_URL}/api/referrals/invite`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: inviteEmail.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setInviteError(data.error ?? "Failed to send invite");
        return;
      }
      if (data.newLink) setReferralLink(data.newLink);
      setInviteEmail("");
      setInviteSuccess(true);
      setTimeout(() => setInviteSuccess(false), 4000);
      await Promise.all([
        fetch(`${BASE_URL}/api/referrals`, { credentials: "include" }).then((r) => r.json()),
      ]).then(([listData]) => {
        if (Array.isArray(listData?.referrals)) setReferrals(listData.referrals);
        if (typeof listData?.totalRewarded === "number") setTotalRewarded(listData.totalRewarded);
      });
    } catch {
      setInviteError("Failed to send invite. Please try again.");
    } finally {
      setInviteSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSendInvite();
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "3rem" }}>
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const totalInvites = referrals.filter((r) => r.type === "invite").length;

  return (
    <div className="space-y-6">
      <Card className="border-border/50 shadow-sm">
        <CardHeader>
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Gift className="h-4 w-4 text-primary" />
            Invite a Contractor
          </CardTitle>
          <CardDescription>
            Enter an email address to send a referral invite. When they subscribe to Pro, you both get one free month.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "stretch" }}>
            <Input
              type="email"
              value={inviteEmail}
              onChange={(e) => { setInviteEmail(e.target.value); setInviteError(null); }}
              onKeyDown={handleKeyDown}
              placeholder="colleague@example.com"
              disabled={inviteSending}
              style={{ flex: 1 }}
            />
            <Button onClick={handleSendInvite} disabled={inviteSending || !inviteEmail.trim()} style={{ flexShrink: 0 }}>
              {inviteSending ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending…</>
              ) : (
                <><Send className="h-4 w-4 mr-2" />Send Invite</>
              )}
            </Button>
          </div>
          {inviteSuccess && (
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.625rem 0.875rem", background: "hsl(142 76% 36% / 0.08)", border: "1px solid hsl(142 76% 36% / 0.2)", borderRadius: "0.5rem", color: "hsl(142 76% 36%)", fontSize: "0.875rem" }}>
              <CheckCheck className="h-4 w-4 flex-shrink-0" />
              Invite sent! Your referral link has been updated.
            </div>
          )}
          {inviteError && (
            <div style={{ padding: "0.625rem 0.875rem", background: "hsl(var(--destructive) / 0.08)", border: "1px solid hsl(var(--destructive) / 0.2)", borderRadius: "0.5rem", color: "hsl(var(--destructive))", fontSize: "0.875rem" }}>
              {inviteError}
            </div>
          )}
          <div style={{ paddingTop: "0.5rem", borderTop: "1px solid hsl(var(--border) / 0.5)" }}>
            <p style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", marginBottom: "0.375rem" }}>Your current referral link:</p>
            <p style={{ fontFamily: "monospace", fontSize: "0.8rem", color: "hsl(var(--foreground))", wordBreak: "break-all" }}>{referralLink ?? "—"}</p>
          </div>
          <div style={{ display: "flex", gap: "1.5rem" }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "hsl(var(--foreground))" }}>{totalRewarded}</div>
              <div style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>Successful referrals</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "hsl(var(--foreground))" }}>{totalInvites}</div>
              <div style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>Total invites sent</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {referrals.length > 0 && (
        <Card className="border-border/50 shadow-sm">
          <CardHeader>
            <CardTitle className="text-base font-semibold">Invite History</CardTitle>
          </CardHeader>
          <CardContent style={{ padding: 0 }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid hsl(var(--border))" }}>
                    <th style={{ textAlign: "left", padding: "0.625rem 1rem", fontWeight: 500, color: "hsl(var(--muted-foreground))", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Contact</th>
                    <th style={{ textAlign: "left", padding: "0.625rem 1rem", fontWeight: 500, color: "hsl(var(--muted-foreground))", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Type</th>
                    <th style={{ textAlign: "left", padding: "0.625rem 1rem", fontWeight: 500, color: "hsl(var(--muted-foreground))", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Date</th>
                    <th style={{ textAlign: "left", padding: "0.625rem 1rem", fontWeight: 500, color: "hsl(var(--muted-foreground))", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {referrals.map((r, i) => (
                    <tr key={`${r.type}-${r.id}`} style={{ borderBottom: i < referrals.length - 1 ? "1px solid hsl(var(--border) / 0.5)" : "none" }}>
                      <td style={{ padding: "0.75rem 1rem", color: "hsl(var(--foreground))" }}>
                        {r.type === "invite"
                          ? <span style={{ fontFamily: "monospace", fontSize: "0.8125rem" }}>{r.email}</span>
                          : r.name ?? <span style={{ color: "hsl(var(--muted-foreground))" }}>—</span>
                        }
                      </td>
                      <td style={{ padding: "0.75rem 1rem" }}>
                        {r.type === "invite" ? (
                          <Badge variant="outline" style={{ fontSize: "0.7rem" }}>
                            <Send className="h-3 w-3 mr-1" />
                            Email invite
                          </Badge>
                        ) : (
                          <Badge variant="secondary" style={{ fontSize: "0.7rem" }}>
                            <Users className="h-3 w-3 mr-1" />
                            Sign-up
                          </Badge>
                        )}
                      </td>
                      <td style={{ padding: "0.75rem 1rem", color: "hsl(var(--muted-foreground))" }}>
                        {new Date(r.createdAt).toLocaleDateString()}
                      </td>
                      <td style={{ padding: "0.75rem 1rem" }}>
                        {r.status === "rewarded" ? (
                          <Badge variant="secondary" style={{ background: "hsl(142 76% 36% / 0.1)", color: "hsl(142 76% 36%)", border: "1px solid hsl(142 76% 36% / 0.2)" }}>
                            <Check className="h-3 w-3 mr-1" />
                            Rewarded
                          </Badge>
                        ) : r.status === "invited" ? (
                          <Badge variant="outline" style={{ color: "hsl(var(--muted-foreground))" }}>
                            <Send className="h-3 w-3 mr-1" />
                            Invited
                          </Badge>
                        ) : (
                          <Badge variant="outline" style={{ color: "hsl(var(--muted-foreground))" }}>
                            Pending first payment
                          </Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {referrals.length === 0 && (
        <div style={{ textAlign: "center", padding: "2rem", color: "hsl(var(--muted-foreground))" }}>
          <Gift className="h-8 w-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No invites sent yet. Enter an email above to get started!</p>
        </div>
      )}
    </div>
  );
}

const CHANGE_TYPE_LABEL: Record<ChangeType, string> = { new: "New", improved: "Improved", fixed: "Fixed" };
const CHANGE_TYPE_CLASS: Record<ChangeType, string> = {
  new: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
  improved: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  fixed: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
};

function formatReleaseDate(release: { version: string; date: string }): string {
  const [y, m, d] = release.date.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  // Monthly summaries (version YYYY.MM) show just the month.
  return release.version.split(".").length === 2
    ? date.toLocaleDateString("en-US", { month: "long", year: "numeric" })
    : date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

function WhatsNewTab() {
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        You're on QuoTrak version <span className="font-medium text-foreground">{CURRENT_VERSION}</span>. Here's what has changed in each update.
      </p>
      {RELEASES.map((release, i) => (
        <Card key={release.version} className="border-border/50 shadow-sm">
          <CardHeader className="pb-2 pt-4">
            <CardTitle className="text-sm font-semibold flex flex-wrap items-center gap-2">
              <span>Version {release.version}</span>
              {i === 0 && <Badge className="text-xs font-normal">Latest</Badge>}
              <span className="ml-auto text-xs font-normal text-muted-foreground">{formatReleaseDate(release)}</span>
            </CardTitle>
            {release.title && <CardDescription>{release.title}</CardDescription>}
          </CardHeader>
          <CardContent className="pb-4 pt-0">
            <ul className="space-y-2">
              {release.changes.map((change, j) => (
                <li key={j} className="flex items-start gap-2 text-sm">
                  <span className={`shrink-0 rounded px-1.5 py-0.5 text-[11px] font-semibold leading-tight ${CHANGE_TYPE_CLASS[change.type]}`}>
                    {CHANGE_TYPE_LABEL[change.type]}
                  </span>
                  <span className="text-foreground">{change.text}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function SupportPage() {
  usePageTitle("Support");
  const { user } = useAuth();
  const { totalUnread } = useSupportNotifications();
  const { guideState } = useSetupGuide();
  const [activeTab, setActiveTab] = useState<Tab>("guides");

  const isGuideComplete =
    guideState === null ||
    guideState.steps.every(
      (step) =>
        guideState.completedSteps.includes(step) ||
        guideState.dismissedSteps.includes(step)
    );

  useEffect(() => {
    if (isGuideComplete && activeTab === "setup-guide") {
      setActiveTab("guides");
    }
  }, [isGuideComplete, activeTab]);

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
  };

  const allTabs: { id: Tab; label: string; icon: React.ElementType }[] = [
    { id: "guides", label: "Guides", icon: FileText },
    { id: "support", label: "Support", icon: Mail },
    { id: "referrals", label: "Referrals", icon: Gift },
    { id: "feature-requests", label: "Feature Requests", icon: Lightbulb },
    { id: "ask-ai", label: "Ask AI", icon: Bot },
    { id: "setup-guide", label: "Setup Guide", icon: BookOpen },
    { id: "whats-new", label: "What's New", icon: Sparkles },
  ];

  const tabs = allTabs.filter((tab) => tab.id !== "setup-guide" || !isGuideComplete);

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-foreground flex items-center gap-2">
          <HelpCircle className="w-8 h-8 text-primary" />
          Help & Support
        </h1>
        <p className="text-muted-foreground mt-1 max-w-xl">
          Step-by-step guides, contact support, or ask our AI assistant anything about QuoTrak.
        </p>
      </div>

      <section>
        <div className="flex gap-1 p-1 bg-border rounded-lg w-full overflow-x-auto mb-6">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => handleTabChange(id)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-base font-medium transition-all whitespace-nowrap ${
                activeTab === id
                  ? "bg-card text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon style={{ width: "1rem", height: "1rem" }} />
              {label}
              {id === "support" && totalUnread > 0 && (
                <span style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "1.125rem",
                  height: "1.125rem",
                  padding: "0 0.2rem",
                  borderRadius: "9999px",
                  background: "#ef4444",
                  color: "#fff",
                  fontSize: "0.625rem",
                  fontWeight: 700,
                  marginLeft: "0.125rem",
                }}>
                  {totalUnread > 99 ? "99+" : totalUnread}
                </span>
              )}
            </button>
          ))}
        </div>

        {activeTab === "guides" && <GuidesTab />}
        {activeTab === "support" && <SupportTab />}
        {activeTab === "referrals" && <ReferralsTab />}
        {activeTab === "feature-requests" && <FeatureRequestsTab />}
        {activeTab === "ask-ai" && <AskAITab />}
        {activeTab === "setup-guide" && <SetupGuideTab />}
        {activeTab === "whats-new" && <WhatsNewTab />}
      </section>
    </div>
  );
}
